import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Inbox, RefreshCw, MessageCircle, Mail, Instagram, Globe2,
    CheckCircle2, ArchiveX, Award, Filter, Sparkles, Package, Wand2, Lock,
} from "lucide-react";
import { toast } from "sonner";
import {
    fetchInquiries,
    fetchInquiryStats,
    updateInquiryStatus,
} from "@/lib/adminApi";

const STATUS_OPTIONS = [
    { value: "new", label: "New", icon: Sparkles },
    { value: "contacted", label: "Contacted", icon: MessageCircle },
    { value: "won", label: "Won", icon: Award },
    { value: "archived", label: "Archived", icon: ArchiveX },
];

export default function AdminInquiries() {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState("all");
    const [filterKind, setFilterKind] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (filterType !== "all") params.type = filterType;
        if (filterKind !== "all") params.kind = filterKind;
        if (filterStatus !== "all") params.status = filterStatus;
        try {
            const [list, statsRes] = await Promise.all([
                fetchInquiries(params),
                fetchInquiryStats(),
            ]);
            setItems(list.items || []);
            setStats(statsRes);
        } catch (err) {
            toast.error("Could not load inquiries");
        } finally {
            setLoading(false);
        }
    }, [filterType, filterKind, filterStatus]);

    useEffect(() => { load(); }, [load]);

    const updateStatus = async (id, status) => {
        try {
            await updateInquiryStatus(id, status);
            setItems((arr) => arr.map((i) => (i.id === id ? { ...i, status } : i)));
            if (selected?.id === id) setSelected((s) => ({ ...s, status }));
            toast.success(`Marked ${status}`);
            // refresh stats lazily
            fetchInquiryStats().then(setStats).catch(() => null);
        } catch (err) {
            toast.error("Update failed");
        }
    };

    return (
        <div className="px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="pg-eyebrow">Studio CMS · Lead Intake</span>
                    <h1
                        className="font-display mt-4 text-3xl sm:text-4xl"
                        style={{ color: "var(--pg-fg)" }}
                        data-testid="admin-inquiries-headline"
                    >
                        Inquiries
                    </h1>
                </div>
                <button
                    type="button"
                    onClick={load}
                    data-testid="admin-inquiries-refresh"
                    className="pg-btn-ghost"
                >
                    <RefreshCw className="h-4 w-4" /> Refresh
                </button>
            </div>

            {/* Stats */}
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                <StatCard label="Total" value={stats?.total ?? "—"} />
                <StatCard label="Package" value={stats?.by_kind?.package ?? 0} />
                <StatCard label="Custom" value={stats?.by_kind?.custom ?? 0} />
                <StatCard label="Branding" value={stats?.by_type?.branding ?? 0} />
                <StatCard label="Video" value={stats?.by_type?.video ?? 0} />
                <StatCard label="New" value={stats?.by_status?.new ?? 0} accent />
            </div>

            {/* Filters */}
            <div className="mt-7 flex flex-wrap items-center gap-3">
                <Filter className="h-4 w-4" style={{ color: "var(--pg-fg-muted)" }} />
                <Pills
                    label="Kind"
                    value={filterKind}
                    onChange={setFilterKind}
                    testIdPrefix="admin-filter-kind"
                    options={[
                        { value: "all", label: "All" },
                        { value: "package", label: "Package" },
                        { value: "custom", label: "Custom" },
                    ]}
                />
                <Pills
                    label="Type"
                    value={filterType}
                    onChange={setFilterType}
                    testIdPrefix="admin-filter-type"
                    options={[
                        { value: "all", label: "All" },
                        { value: "branding", label: "Branding" },
                        { value: "video", label: "Video" },
                    ]}
                />
                <Pills
                    label="Status"
                    value={filterStatus}
                    onChange={setFilterStatus}
                    testIdPrefix="admin-filter-status"
                    options={[
                        { value: "all", label: "All" },
                        { value: "new", label: "New" },
                        { value: "contacted", label: "Contacted" },
                        { value: "won", label: "Won" },
                        { value: "archived", label: "Archived" },
                    ]}
                />
            </div>

            {/* Table */}
            <div
                className="mt-6 overflow-hidden rounded-2xl border"
                style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
            >
                {loading ? (
                    <div className="flex items-center justify-center px-6 py-16 text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                        Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center px-6 py-20 text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                        <Inbox className="mb-3 h-6 w-6" />
                        No inquiries yet. New leads will appear here automatically.
                    </div>
                ) : (
                    <table className="min-w-full text-sm">
                        <thead
                            className="border-b font-mono text-[10px] uppercase tracking-[0.18em]"
                            style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
                        >
                            <tr>
                                <th className="px-4 py-3 text-left">Client</th>
                                <th className="hidden px-4 py-3 text-left md:table-cell">Brief</th>
                                <th className="hidden px-4 py-3 text-left lg:table-cell">Country</th>
                                <th className="px-4 py-3 text-left">Budget</th>
                                <th className="hidden px-4 py-3 text-left sm:table-cell">Kind · Type</th>
                                <th className="px-4 py-3 text-left">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row) => (
                                <tr
                                    key={row.id}
                                    onClick={() => setSelected(row)}
                                    className="cursor-pointer border-b transition-colors hover:bg-[var(--pg-bg-alt)]"
                                    style={{ borderColor: "var(--pg-border)" }}
                                    data-testid="admin-inquiry-row"
                                    data-inquiry-id={row.id}
                                >
                                    <td className="px-4 py-4">
                                        <div className="font-display text-base" style={{ color: "var(--pg-fg)" }}>{row.full_name}</div>
                                        <div className="text-xs" style={{ color: "var(--pg-fg-muted)" }}>{row.business_name || row.email}</div>
                                    </td>
                                    <td className="hidden px-4 py-4 md:table-cell">
                                        <div className="text-sm" style={{ color: "var(--pg-fg)" }}>
                                            {row.snapshot?.package_name || row.selected_package || "—"}
                                        </div>
                                        <div className="text-xs" style={{ color: "var(--pg-fg-muted)" }}>
                                            {row.snapshot?.price_label || row.package_price || ""}
                                        </div>
                                    </td>
                                    <td className="hidden px-4 py-4 lg:table-cell">
                                        <span className="text-sm" style={{ color: "var(--pg-fg)" }}>{row.detected_country || "—"}</span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <span className="text-sm" style={{ color: "var(--pg-fg)" }}>{row.estimated_budget || row.custom_budget || "—"}</span>
                                    </td>
                                    <td className="hidden px-4 py-4 sm:table-cell">
                                        <div className="flex flex-wrap items-center gap-1">
                                            <KindBadge kind={row.inquiry_kind} />
                                            <TypeBadge type={row.inquiry_type} />
                                        </div>
                                    </td>
                                    <td className="px-4 py-4">
                                        <StatusBadge status={row.status || "new"} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Detail drawer */}
            <AnimatePresence>
                {selected && (
                    <DetailDrawer
                        inquiry={selected}
                        onClose={() => setSelected(null)}
                        onStatusChange={(s) => updateStatus(selected.id, s)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

const StatCard = ({ label, value, accent = false }) => (
    <div
        className="rounded-2xl border p-5"
        style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
    >
        <div className={`font-display text-2xl sm:text-3xl ${accent ? "pg-text-gold-static" : ""}`} style={!accent ? { color: "var(--pg-fg)" } : undefined}>
            {value}
        </div>
        <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--pg-fg-muted)" }}>
            {label}
        </div>
    </div>
);

const Pills = ({ label, value, onChange, options, testIdPrefix }) => (
    <div className="inline-flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>{label}</span>
        <div className="inline-flex gap-1 rounded-full border p-1" style={{ borderColor: "var(--pg-border)" }}>
            {options.map((o) => {
                const active = value === o.value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => onChange(o.value)}
                        data-testid={`${testIdPrefix}-${o.value}`}
                        className="rounded-full px-3 py-1 text-xs font-mono uppercase tracking-[0.16em] transition-colors"
                        style={{
                            background: active ? "var(--pg-fg)" : "transparent",
                            color: active ? "var(--pg-bg)" : "var(--pg-fg-muted)",
                        }}
                    >
                        {o.label}
                    </button>
                );
            })}
        </div>
    </div>
);

const TypeBadge = ({ type }) => {
    const isVideo = (type || "branding") === "video";
    return (
        <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{
                background: isVideo ? "rgba(255,197,139,0.16)" : "rgba(11,59,58,0.12)",
                color: isVideo ? "#B8682B" : "var(--pg-teal)",
            }}
        >
            {isVideo ? "Video" : "Branding"}
        </span>
    );
};

const KindBadge = ({ kind }) => {
    const isCustom = (kind || "package") === "custom";
    const Icon = isCustom ? Wand2 : Package;
    return (
        <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{
                background: isCustom ? "rgba(255,197,139,0.16)" : "color-mix(in srgb, var(--pg-accent) 14%, transparent)",
                color: isCustom ? "#B8682B" : "var(--pg-accent)",
            }}
            data-testid={`admin-kind-badge-${isCustom ? "custom" : "package"}`}
        >
            <Icon className="h-3 w-3" strokeWidth={1.8} /> {isCustom ? "Custom" : "Package"}
        </span>
    );
};

const StatusBadge = ({ status }) => {
    const colorMap = {
        new: { bg: "rgba(246,199,137,0.18)", fg: "#8C5E1E" },
        contacted: { bg: "rgba(59,130,246,0.18)", fg: "#3B82F6" },
        won: { bg: "rgba(34,197,94,0.18)", fg: "#16A34A" },
        archived: { bg: "rgba(120,120,120,0.18)", fg: "var(--pg-fg-muted)" },
    };
    const c = colorMap[status] || colorMap.new;
    return (
        <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{ background: c.bg, color: c.fg }}
        >
            {status}
        </span>
    );
};

const DetailDrawer = ({ inquiry, onClose, onStatusChange }) => {
    const snap = inquiry.snapshot || null;
    const pricingSource = useMemo(() => {
        if (snap && (snap.base_price || snap.estimated_total || snap.is_custom_scope || (snap.addons || []).length)) {
            return snap;
        }
        return {
            base_price: inquiry.base_price,
            addons: inquiry.addons || [],
            addons_total: inquiry.addons_total,
            estimated_total: inquiry.estimated_total,
            is_custom_scope: inquiry.is_custom_scope,
            package_name: inquiry.selected_package,
            package_category: inquiry.package_category,
            price_label: inquiry.package_price,
        };
    }, [snap, inquiry]);

    const items = useMemo(() => [
        ["Full Name", inquiry.full_name],
        ["Business", inquiry.business_name],
        ["Business Type", inquiry.business_type],
        ["Email", inquiry.email],
        ["WhatsApp", inquiry.whatsapp_number],
        ["Instagram", inquiry.instagram_username],
        ["Detected Country", inquiry.detected_country],
        ["Selected Package", inquiry.selected_package || pricingSource.package_name],
        ["Package Category", inquiry.package_category || pricingSource.package_category],
        ["Package Price", inquiry.package_price || pricingSource.price_label],
        ["Base Price", pricingSource.base_price ? `$${Number(pricingSource.base_price).toLocaleString()}` : "—"],
        ["Add-Ons", (pricingSource.addons || []).map((a) => `${a.name} (+$${Number(a.price).toLocaleString()})`).join(", ") || "—"],
        ["Add-Ons Total", pricingSource.addons_total ? `$${Number(pricingSource.addons_total).toLocaleString()}` : "—"],
        ["Estimated Total", pricingSource.estimated_total ? `$${Number(pricingSource.estimated_total).toLocaleString()}` : "—"],
        ["Custom Scope?", pricingSource.is_custom_scope ? "Yes — custom quote required" : "No"],
        ["Services Needed", (inquiry.services_needed || []).join(", ")],
        ["Budget Range", inquiry.budget_range],
        ["Budget (client-picked)", inquiry.estimated_budget || inquiry.custom_budget],
        ["Goals / Vision", inquiry.goals],
        ["What do you need?", inquiry.what_do_you_need],
        ["Project Details", inquiry.project_details],
        ["Preferred Comm", inquiry.preferred_communication],
        ["Website", inquiry.website_link],
        ["FB / IG Links", [inquiry.facebook_link, inquiry.instagram_link].filter(Boolean).join(" · ")],
        ["Expected Timeline", inquiry.expected_timeline],
        ["Submitted", inquiry.submitted_at],
        ["Snapshot Captured", snap?.captured_at || null],
        ["Inquiry ID", inquiry.id],
    ], [inquiry, pricingSource, snap]);

    return (
        <motion.div
            className="fixed inset-0 z-50 flex items-end justify-end bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="admin-inquiry-drawer"
        >
            <motion.aside
                onClick={(e) => e.stopPropagation()}
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="flex h-full w-full flex-col overflow-y-auto sm:w-[520px]"
                style={{ background: "var(--pg-bg)", borderLeft: "1px solid var(--pg-border)" }}
            >
                <div className="flex items-start justify-between gap-3 border-b px-5 py-5 sm:px-7 sm:py-6" style={{ borderColor: "var(--pg-border)" }}>
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <KindBadge kind={inquiry.inquiry_kind} />
                            <TypeBadge type={inquiry.inquiry_type} />
                            <StatusBadge status={inquiry.status || "new"} />
                        </div>
                        <h2 className="font-display mt-3 text-2xl" style={{ color: "var(--pg-fg)" }}>{inquiry.full_name}</h2>
                        <p className="text-sm" style={{ color: "var(--pg-fg-muted)" }}>{inquiry.business_name || inquiry.email}</p>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="rounded-full p-2" style={{ color: "var(--pg-fg-muted)" }}>✕</button>
                </div>

                <div className="flex flex-wrap gap-2 border-b px-5 py-4 sm:px-7" style={{ borderColor: "var(--pg-border)" }}>
                    {inquiry.whatsapp_number && (
                        <a href={`https://wa.me/${inquiry.whatsapp_number.replace(/[^\d]/g,"")}`} target="_blank" rel="noopener noreferrer" className="pg-btn-ghost">
                            <MessageCircle className="h-4 w-4" /> WhatsApp
                        </a>
                    )}
                    {inquiry.instagram_username && (
                        <a href={`https://instagram.com/${(inquiry.instagram_username || "").replace(/^@/,"")}`} target="_blank" rel="noopener noreferrer" className="pg-btn-ghost">
                            <Instagram className="h-4 w-4" /> Instagram
                        </a>
                    )}
                    {inquiry.email && (
                        <a href={`mailto:${inquiry.email}`} className="pg-btn-ghost">
                            <Mail className="h-4 w-4" /> Email
                        </a>
                    )}
                </div>

                <div className="px-5 py-5 sm:px-7">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>Update Status</span>
                    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="admin-status-buttons">
                        {STATUS_OPTIONS.map((s) => (
                            <button
                                key={s.value}
                                type="button"
                                onClick={() => onStatusChange(s.value)}
                                data-testid={`admin-status-${s.value}`}
                                className="flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-mono uppercase tracking-[0.16em] transition-colors"
                                style={{
                                    borderColor: (inquiry.status || "new") === s.value ? "var(--pg-accent)" : "var(--pg-border)",
                                    background: (inquiry.status || "new") === s.value ? "color-mix(in srgb, var(--pg-accent) 12%, transparent)" : "transparent",
                                    color: "var(--pg-fg)",
                                }}
                            >
                                <s.icon className="h-3 w-3" /> {s.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 px-5 pb-10 sm:px-7" data-testid="admin-inquiry-details">
                    {/* Pricing breakdown highlight (prefers server-authored snapshot when available) */}
                    {(pricingSource.base_price || pricingSource.estimated_total || pricingSource.is_custom_scope || (pricingSource.addons || []).length > 0) && (
                        <div
                            className="mb-5 rounded-2xl border p-5"
                            style={{
                                borderColor: "color-mix(in srgb, var(--pg-accent) 35%, var(--pg-border))",
                                background: "linear-gradient(135deg, color-mix(in srgb, var(--pg-accent) 8%, transparent) 0%, var(--pg-surface) 100%)",
                            }}
                            data-testid="admin-inquiry-pricing"
                        >
                            <div className="flex items-center gap-2">
                                {snap && <Lock className="h-3 w-3" style={{ color: "var(--pg-accent)" }} />}
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                                    Pricing Breakdown {snap ? "· Snapshot" : ""}
                                </span>
                            </div>
                            {pricingSource.is_custom_scope ? (
                                <>
                                    <div className="font-display mt-2 text-xl pg-text-gold-static">Custom Quote Required</div>
                                    {(inquiry.estimated_budget || inquiry.custom_budget) && (
                                        <div className="mt-1 text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                                            Client budget: <span style={{ color: "var(--pg-fg)" }}>{inquiry.estimated_budget || inquiry.custom_budget}</span>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="mt-3 flex flex-wrap items-baseline justify-between gap-2 text-sm">
                                        <span style={{ color: "var(--pg-fg-muted)" }}>Base Price</span>
                                        <span className="pg-price-num pg-text-gold-static text-lg">${Number(pricingSource.base_price || 0).toLocaleString()}</span>
                                    </div>
                                    {(pricingSource.addons || []).length > 0 && (
                                        <ul className="mt-2 space-y-1 text-[13px]">
                                            {(pricingSource.addons || []).map((a) => (
                                                <li key={a.id || a.name} className="flex items-baseline justify-between gap-2" style={{ color: "var(--pg-fg)" }}>
                                                    <span className="truncate">+ {a.name}</span>
                                                    <span className="font-mono text-xs" style={{ color: "var(--pg-accent)" }}>+${Number(a.price).toLocaleString()}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {(pricingSource.addons_total || 0) > 0 && (
                                        <div className="mt-2 flex items-baseline justify-between text-sm">
                                            <span style={{ color: "var(--pg-fg-muted)" }}>Add-ons Total</span>
                                            <span className="font-display" style={{ color: "var(--pg-fg)" }}>${Number(pricingSource.addons_total).toLocaleString()}</span>
                                        </div>
                                    )}
                                    <div className="mt-3 flex items-baseline justify-between border-t pt-3" style={{ borderColor: "var(--pg-border)" }}>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-accent)" }}>Estimated Total</span>
                                        <span className="pg-price-num pg-text-gold-static text-3xl" data-testid="admin-inquiry-total">
                                            ${Number(pricingSource.estimated_total || (pricingSource.base_price || 0) + (pricingSource.addons_total || 0)).toLocaleString()}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    <table className="w-full text-sm">
                        <tbody>
                            {items.filter(([_, v]) => v).map(([k, v]) => (
                                <tr key={k} className="border-b" style={{ borderColor: "var(--pg-border)" }}>
                                    <td className="py-2.5 pr-3 font-mono text-[10px] uppercase tracking-[0.18em] align-top w-1/3" style={{ color: "var(--pg-fg-muted)" }}>{k}</td>
                                    <td className="py-2.5 align-top whitespace-pre-wrap break-words" style={{ color: "var(--pg-fg)" }}>{String(v)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </motion.aside>
        </motion.div>
    );
};
